import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import classNames from "./Usage.module.css";
import api, {
  GetOrgListResponse,
  GetUsageResponse,
} from "../../../hooks/api-new";
import { useAxios } from "../../../hooks/axios";
import Loader from "../../common/Loader";
import { getLanguageFlag, getLanguageName } from "../../../types";
import { Link } from "react-router-dom";
import TimeRangeSelect, { TimeRange } from "./usage/TimeRangeSelect";
import LanguageUsageGraph from "./usage/LanguageUsageGraph";
import ParticipantUsageGraph from "./usage/ParticipantUsageGraph";
import * as Tooltip from "@radix-ui/react-tooltip";
import { IoIosHelpCircle } from "react-icons/io";
import AdminUserSelect from "./usage/AdminUserSelect";
import { useCurrentPlan } from "../../context/organization/organization";
import GlobalOrganizationSelect from "./usage/GlobalOrganizationSelect";
import useUser from "../../../hooks/user";

function UsageStatCard(props: {
  title: ReactNode | string;
  stat: ReactNode | string;
  insights: ReactNode | string;
  tooltip: string;
}) {
  return (
    <div className={classNames.UsageStatCard}>
      <div className={classNames.Title}>
        <h2>{props.title}</h2>
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild className={classNames.TooltipTrigger}>
              <div>
                <IoIosHelpCircle />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className={classNames.TooltipContent}
                side="top"
                align="center"
                sideOffset={5}
              >
                {props.tooltip}
                <Tooltip.Arrow className={classNames.TooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>

      <h3>{props.stat}</h3>
      <h4>{props.insights}</h4>
    </div>
  );
}

export default function Usage() {
  const [usage, setUsage] = useState<GetUsageResponse["usage"]>();
  const [organization, setOrganization] =
    useState<GetUsageResponse["organization"]>();
  const [loading, setLoading] = useState(false);

  const axios = useAxios();
  const getOrganization = api.useGetOrganization(axios);
  const getUsage = api.useGetUsage(axios);
  const getAdminUsage = api.useGetAdminUsage(axios);

  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>({
    name: "last-24-hours",
    start: new Date(new Date().setHours(new Date().getHours() - 24)),
    end: new Date(new Date().setMinutes(new Date().getMinutes() + 5)),
  });
  const [selectedMemberId, setSelectedMemberId] = useState<string>();

  // Admin org selector
  const [selectedOrgId, setSelectedOrgId] = useState<string>();

  const [user] = useUser();
  const isAdmin = useMemo(() => {
    return (
      user.auth &&
      ["jacob.greenway@byrdhouseapp.com", "snow.huo@byrdhouseapp.com"].includes(
        user.auth.email
      )
    );
  }, [user.auth]);

  const [orgs, setOrgs] = useState<GetOrgListResponse["orgs"]>();
  const getOrgList = api.useGetOrgList(axios);

  // Load pro orgs if admin
  useEffect(() => {
    if (isAdmin) {
      (async () => {
        const response = await getOrgList();
        setOrgs(response.data.orgs);
      })();
    }
  }, [isAdmin, getOrgList]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = !selectedOrgId
          ? await getUsage({
              user_id: selectedMemberId,
              start_time: selectedTimeRange.start,
              end_time: selectedTimeRange.end,
            })
          : await getAdminUsage({
              org_id: selectedOrgId,
              user_id: selectedMemberId,
              start_time: selectedTimeRange.start,
              end_time: selectedTimeRange.end,
            });
        setUsage(response.data.usage);
        setOrganization(response.data.organization);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    getOrganization,
    getUsage,
    getAdminUsage,
    selectedMemberId,
    selectedTimeRange,
    selectedOrgId,
  ]);

  const toCSV = useCallback(
    <T extends Record<string, unknown>>(data: T[]): string => {
      if (!Array.isArray(data) || data.length === 0 || !data[0]) {
        return "";
      }

      const headers = Object.keys(data[0]) as (keyof T)[];
      const csvRows = data.map((row) =>
        headers.map((field) => row?.[field] ?? "").join(",")
      );

      return [headers.join(","), ...csvRows].join("\n");
    },
    []
  );

  const mergeUsageIntervals = useCallback(
    (usage: GetUsageResponse["usage"], filterByHostId?: string) => {
      const merged = usage
        // Used to sort admins be highest usage first
        .filter((u) => !filterByHostId || u.host_id === filterByHostId)
        // Step 1: Filter to just speaker usage (mic on and translating)
        .filter((u) => u.participant_type === "speaker")
        // Step 2: Map to intervals with numeric timestamps
        .map(({ room_id, created_at, ended_at, email }) => ({
          room_id,
          email,
          start: new Date(created_at).getTime(),
          end: new Date(ended_at).getTime(),
        }))
        // Step 3: Sort intervals by start time
        .sort((a, b) => a.start - b.start)
        // Step 4: Merge overlapping intervals
        .reduce<
          { room_id: string; start: number; end: number; email: string }[]
        >((merged, interval) => {
          const last = merged[merged.length - 1];
          if (
            last &&
            last.room_id === interval.room_id &&
            last.end >= interval.start
          ) {
            // Same room and overlapping -- merge
            last.end = Math.max(last.end, interval.end);
          } else {
            // Different room or no overlap -- push as new
            merged.push(interval);
          }
          return merged;
        }, []);

      const data = merged.map((interval) => ({
        room_id: interval.room_id,
        date: new Date(interval.start).toISOString().split("T")[0],
        admin: interval.email,
        usage_in_minutes: ((interval.end - interval.start) / 1000 / 60).toFixed(
          2
        ),
      }));
      const csv = toCSV(data);
      console.debug("translation intervals", csv);

      return merged;
    },
    [toCSV]
  );

  // Intervals with start/end numeric timestamps of translation usage (at least one speaker active)
  const translationUsageIntervals = useMemo(() => {
    if (!usage) {
      return [];
    }

    return mergeUsageIntervals(usage);
  }, [usage, mergeUsageIntervals]);

  const totalSecondsGroupedByAdmin = useMemo(() => {
    if (!organization || !usage) {
      return;
    }

    const grouped = organization.members.map((member) => ({
      id: member.id,
      totalSeconds:
        mergeUsageIntervals(usage, member.id).reduce(
          (total, { start, end }) => total + (end - start),
          0
        ) / 1000,
    }));

    return grouped;
  }, [mergeUsageIntervals, organization, usage]);

  const sortedAdmins = useMemo(() => {
    if (!organization || !totalSecondsGroupedByAdmin) {
      return;
    }

    // Turn into a map for performance
    const usageMap = new Map(
      totalSecondsGroupedByAdmin.map(({ id, totalSeconds }) => [
        id,
        totalSeconds,
      ])
    );

    return [...organization.members].sort((a, b) => {
      const aUsage = usageMap.get(a.id) ?? 0;
      const bUsage = usageMap.get(b.id) ?? 0;
      return bUsage - aUsage;
    });
  }, [organization, totalSecondsGroupedByAdmin]);

  const totalMinutes = useMemo(() => {
    const totalSeconds =
      translationUsageIntervals
        // Calculate total duration from merged intervals
        .reduce((total, { start, end }) => total + (end - start), 0) / 1000;
    return Math.round(totalSeconds / 60);
  }, [translationUsageIntervals]);

  // Intervals with start/end of any time a language is selected AND translator is on (within a translation usage interval, see above)
  const languageUsageIntervals = useMemo(() => {
    if (!usage) {
      return [];
    }

    return Object.entries(
      usage
        // Step 1: Group by languages
        .reduce(
          (
            acc: { [language: string]: { start: number; end: number }[] },
            { language, created_at, ended_at }
          ) => {
            const interval = {
              start: new Date(created_at).getTime(),
              end: new Date(ended_at).getTime(),
            };

            if (!acc[language]) {
              acc[language] = [];
            }
            acc[language].push(interval);

            return acc;
          },
          {}
        )
    ).map(([language, intervals]) => {
      // Step 2: Sort intervals by start time
      intervals.sort((a, b) => a.start - b.start);

      // Step 3: Merge overlapping intervals
      const mergedIntervals = intervals.reduce(
        (merged: { start: number; end: number }[], current) => {
          const last = merged[merged.length - 1];

          if (last && current.start <= last.end) {
            // Overlap detected, merge intervals
            last.end = Math.max(last.end, current.end);
          } else {
            // No overlap, add the current interval
            merged.push(current);
          }

          return merged;
        },
        []
      );

      // Step 4: Only use intervals within translation usage intervals and clamp start/end
      const intervalsClampedWithinTranslation = mergedIntervals.flatMap(
        (interval) => {
          // Clamp the language usage to the translation usage
          return translationUsageIntervals
            .filter(
              ({ start: translatorStart, end: translatorEnd }) =>
                interval.start < translatorEnd && interval.end > translatorStart
            )
            .flatMap(({ start: translatorStart, end: translatorEnd }) => {
              // For each relevant translation interval, clamp to the boundaries
              const clampedStart = Math.max(interval.start, translatorStart);
              const clampedEnd = Math.min(interval.end, translatorEnd);
              return { start: clampedStart, end: clampedEnd };
            });
        }
      );

      return { language, intervals: intervalsClampedWithinTranslation };
    });
  }, [usage, translationUsageIntervals]);

  // Top languages by usage
  const topLanguages = useMemo(() => {
    return (
      Object.entries(
        // Step 1: Calculate total usage for all languages
        languageUsageIntervals.reduce(
          (acc: { [key: string]: number }, { language, intervals }) => {
            // Calculate total usage for the current language
            const totalUsage = intervals.reduce((sum, { start, end }) => {
              return sum + (end - start); // Add up interval durations
            }, 0);

            // Update the accumulator with total usage
            acc[language] = (acc[language] || 0) + totalUsage;

            return acc;
          },
          {}
        )
      )
        // Step 2: Sort languages by usage in descending order
        .sort(
          ([, totalSecondsA], [, totalSecondsB]) =>
            totalSecondsB - totalSecondsA
        )
        // Step 3: Map to array of languages
        .map(([language]) => language)
    );
  }, [languageUsageIntervals]);

  // Organization minutes left
  const minutesLeft = useMemo(() => {
    if (!organization) {
      return 0;
    }
    return Math.round(organization.translation_seconds_remaining / 60);
  }, [organization]);

  const currentPlan = useCurrentPlan(organization);

  if (!organization || !usage || loading) {
    return <Loader />;
  }

  return (
    <div className={classNames.Usage}>
      <h1>Usage</h1>
      <div className={classNames.Filters}>
        {isAdmin && orgs && (
          <GlobalOrganizationSelect
            orgs={orgs}
            selectedOrgId={selectedOrgId}
            onChange={(orgId?: string) => setSelectedOrgId(orgId)}
          />
        )}
        <TimeRangeSelect
          selectedTimeRange={selectedTimeRange}
          onChange={(value) => setSelectedTimeRange(value)}
        />
        <AdminUserSelect
          selectedAdminId={selectedMemberId}
          admins={sortedAdmins ?? organization.members}
          onChange={(adminId) => setSelectedMemberId(adminId)}
        />
      </div>
      <div className={classNames.UsageStatCards}>
        <UsageStatCard
          title={"Total minutes translated"}
          stat={`${totalMinutes} minute${totalMinutes !== 1 ? "s" : ""}`}
          insights={""}
          tooltip="Total translated minutes include the active translation time during meetings or events, excluding periods when speakers are muted or the translator is turned off."
        />
        <UsageStatCard
          title={"Languages used"}
          stat={topLanguages.length}
          insights={
            !!topLanguages.length && (
              <div>
                <div>Top languages:</div>
                <div>
                  {topLanguages.slice(0, 3).map((l) => (
                    <div key={l}>
                      {getLanguageFlag(l) + " " + getLanguageName(l)}
                    </div>
                  ))}
                </div>
              </div>
            )
          }
          tooltip="Languages chosen by any participant during the specified time period."
        />
        <UsageStatCard
          title={"Total minutes left"}
          stat={`${minutesLeft} minute${minutesLeft !== 1 ? "s" : ""}`}
          insights={
            <div>
              <div>
                Current plan:{" "}
                {currentPlan &&
                  currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
              </div>
              <div>
                <Link style={{ textDecoration: "underline" }} to={"/billing"}>
                  Upgrade
                </Link>{" "}
                for more minutes
              </div>
            </div>
          }
          tooltip="Total translation minutes remaining in your subscription plan. This number stays constant and does not change based on the selected time period."
        />
      </div>
      <LanguageUsageGraph
        timeRange={selectedTimeRange}
        languageUsageIntervals={languageUsageIntervals}
      />
      <ParticipantUsageGraph timeRange={selectedTimeRange} usage={usage} />
    </div>
  );
}
